// pages/mypage/MyPageAttendance.tsx
import { useEffect, useState } from "react";
import MyPageLayout from "../../components/mypage/MyPageLayout";
import { getApiUrl, fetchWithAuth } from "../../api";

interface AttendanceMonthlyResponse {
  year: number;
  month: number;
  attendance_dates: string[]; // ["2026-06-01", ...]
  total_count: number;
}

const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];

export default function MyPageAttendance() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState<AttendanceMonthlyResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const res = await fetchWithAuth(
          getApiUrl(`/marubit/api/attendance/monthly?year=${year}&month=${month}`)
        );
        if (res.ok) setData(await res.json());
      } catch (e) {
        console.error("출석 로딩 실패:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [year, month]);

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  };

  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const attendedSet = new Set(
    (data?.attendance_dates ?? []).map(d => new Date(d).getDate())
  );

  const calendarCells: (number | null)[] = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  return (
    <MyPageLayout title="출석현황">
      <div className="flex flex-col gap-5">

        {/* 요약 */}
        <div className="bg-white rounded-2xl border border-[#f0ebe7] shadow-[0_2px_12px_rgba(136,114,103,0.08)] px-7 py-5 flex items-center justify-between">
          <div>
            <p className="m-0 text-[13px] text-gray-400 font-medium">{year}년 {month}월 출석일수</p>
            <p className="m-0 text-[28px] font-black text-[#887267] mt-1">
              {loading ? "-" : (data?.total_count ?? 0)}
              <span className="text-[16px] font-bold ml-1">일</span>
            </p>
          </div>
          <div className="w-[56px] h-[56px] rounded-full bg-[#fdf7f0] border border-[#f0ebe7] flex items-center justify-center">
            <svg viewBox="0 0 24 24" width={28} height={28} fill="none" stroke="#887267" strokeWidth={1.5}>
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
              <path d="M9 16l2 2 4-4" />
            </svg>
          </div>
        </div>

        {/* 달력 */}
        <div className="bg-white rounded-2xl border border-[#f0ebe7] shadow-[0_2px_12px_rgba(136,114,103,0.08)] overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#f5f0ec]">
            <button onClick={prevMonth} className="w-8 h-8 rounded-full hover:bg-[#f5f0ec] flex items-center justify-center transition-colors border-none bg-transparent cursor-pointer">
              <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="#887267" strokeWidth={2}><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <p className="m-0 text-[16px] font-bold text-[#3d2f2a]">{year}년 {month}월</p>
            <button onClick={nextMonth} className="w-8 h-8 rounded-full hover:bg-[#f5f0ec] flex items-center justify-center transition-colors border-none bg-transparent cursor-pointer">
              <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="#887267" strokeWidth={2}><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          </div>

          <div className="px-6 py-5">
            <div className="grid grid-cols-7 mb-2">
              {WEEKDAYS.map((d, i) => (
                <div key={d} className={`text-center text-[12px] font-semibold py-2 ${i === 0 ? "text-red-400" : i === 6 ? "text-blue-400" : "text-gray-400"}`}>
                  {d}
                </div>
              ))}
            </div>

            {loading ? (
              <div className="py-8 text-center text-[14px] text-gray-400">불러오는 중...</div>
            ) : (
              <div className="grid grid-cols-7 gap-y-1">
                {calendarCells.map((day, idx) => {
                  if (!day) return <div key={`empty-${idx}`} />;
                  const isToday = year === now.getFullYear() && month === now.getMonth() + 1 && day === now.getDate();
                  const attended = attendedSet.has(day);
                  const col = idx % 7;

                  return (
                    <div key={day} className="flex flex-col items-center py-1">
                      <div className={[
                        "w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-medium",
                        attended && isToday ? "bg-[#887267] text-white font-bold ring-2 ring-[#f5a623] ring-offset-1" :
                        attended ? "bg-[#887267] text-white font-bold" :
                        isToday ? "border-2 border-[#887267] text-[#887267]" :
                        col === 0 ? "text-red-400" :
                        col === 6 ? "text-blue-400" :
                        "text-[#3d2f2a]",
                      ].join(" ")}>
                        {day}
                      </div>
                      {attended && <div className="w-1 h-1 rounded-full bg-[#f5a623] mt-[2px]" />}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="px-6 pb-5 flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-[#887267]" />
              <span className="text-[12px] text-gray-400">출석</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full border-2 border-[#887267]" />
              <span className="text-[12px] text-gray-400">오늘</span>
            </div>
          </div>
        </div>

      </div>
    </MyPageLayout>
  );
}